import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUser } from "../Context/UserContext";
import { useCompany } from "../Context/CompanyContext";
import { useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react"; // 🔹 Correcto uso de MSAL
import PageLayout from "../components/layout/PageLayout";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import { Eye, EyeOff } from "lucide-react";
import API_URL from "../config";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const Login = () => {
  const { fetchCompanies } = useCompany(); // Carga de empresas asociadas
  const { instance } = useMsal(); // 🔹 Obtener la instancia de MSAL

  const { fetchUserInfo, user } = useUser(); // Carga de la info básica del usuario
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({ correo: "", contrasena: "" });
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Mostrar modal de carga

    try {
      const response = await fetch(`${API_URL}/user/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      console.log("response:", response);

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error);
        return;
      }

      const data = await response.json();

      if (data.id_user) {
        const userId = String(data.id_user);

        // Guardar tokens y datos del usuario en localStorage
        localStorage.setItem(`access_token_${userId}`, data.access_token);
        localStorage.setItem(`refresh_token_${userId}`, data.refresh_token);
        localStorage.setItem("current_user_id", userId);
      } else {
        console.error("Error: id_user no es válido", data);
      }

      // Llamar a fetchUserInfo para cargar la información básica
      await fetchUserInfo();
      fetchCompanies();

      // Redirigir al perfil del usuario
      navigate(`/menu/${data.id_user}/${data.nombre}-${data.apellido}`);
    } catch (error) {
      setError("Error en la conexión con el servidor");
    } finally {
      setIsLoading(false); // Ocultar modal de carga
    }
  };

  const handleOAuthLogin = async (provider, token) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/user/${provider}/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) {
        throw new Error(`Error en la autenticación con ${provider}`);
      }

      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem(`access_token_${data.id_user}`, data.access_token);
        localStorage.setItem(
          `refresh_token_${data.id_user}`,
          data.refresh_token
        );
        localStorage.setItem("current_user_id", data.id_user);

        // Cargar información del usuario
        await fetchUserInfo();
        fetchCompanies();

        // Redirigir al usuario al menú
        navigate(`/menu/${data.id_user}/${data.nombre}-${data.apellido}`);
      }
    } catch (error) {
      console.error(`Error en el login con ${provider}:`, error);
      setError(`Error en la autenticación con ${provider}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      const loginResponse = await instance.loginPopup({
        scopes: ["openid", "profile", "email"],
        prompt: "select_account",
      });

      console.log("Microsoft loginResponse:", loginResponse);

      if (!loginResponse.idToken) {
        throw new Error("No se obtuvo idToken de Microsoft");
      }

      // Enviar idToken al backend
      await handleOAuthLogin("microsoft", loginResponse.idToken);
    } catch (error) {
      console.error("Error en la autenticación con Microsoft:", error);
      setError("Error en la autenticación con Microsoft");
    }
  };

  const handleGoogleLogin = async (tokenResponse) => {
    setIsLoading(true); // Mostrar modal de carga
    console.log("Google login success:", tokenResponse);

    try {
      const res = await fetch(`${API_URL}/user/google/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: tokenResponse.code }), //✅ Ahora usas correctamente el código de autorización
      });

      if (!res.ok) {
        throw new Error(`Error en la solicitud: ${res.statusText}`);
      }

      const data = await res.json();
      console.log("Respuesta del backend:", data);

      if (data.id_user) {
        const userId = String(data.id_user);

        // Guardar tokens en localStorage
        localStorage.setItem(`access_token_${userId}`, data.access_token);
        localStorage.setItem(`refresh_token_${userId}`, data.refresh_token);
        localStorage.setItem("current_user_id", userId);

        console.log("✅ Tokens guardados en localStorage");

        // 🔹 Asegurarnos de cargar la información del usuario ANTES de redirigir
        await fetchUserInfo();
        fetchCompanies();

        console.log("✅ Información del usuario y empresas cargada");

        // 🔹 Ahora sí redirigir
        navigate(`/menu/${data.id_user}/${data.nombre}-${data.apellido}`);
      } else {
        console.error("Error: No se recibió un token de acceso válido", data);
      }
    } catch (error) {
      console.error("Error en el login con Google", error);
    } finally {
      setIsLoading(false); // Ocultar modal de carga
    }
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: handleGoogleLogin,
    onError: () => console.log("Error en la autenticación con Google"),
    flow: "auth-code", // 🔥 Cambia a 'auth-code' para obtener un JWT id_token
  });

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleRedirect = () => {
    navigate("/register");
  };

  return (
    <PageLayout>
      <Helmet>
        <title>Iniciar Sesión - Procu</title>
        <meta
          name="description"
          content="Inicia sesión en Procu y accede a la mejor plataforma para conectar empresas con proveedores. Gestiona tus licitaciones y postulaciones de manera eficiente."
        />
        <meta name="keywords" content="iniciar sesión, login, Procu, proveedores, licitaciones, negocios, B2B" />
      </Helmet>

      {isLoading && <LoadingOverlay />}

      <div className="flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <h2 className="text-center text-2xl font-semibold text-foreground">Iniciar Sesión</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Recuerda que para usar las funcionalidades de la página tienes que iniciar sesión.
              Puedes{" "}
              <Link className="font-semibold text-primary hover:underline" to="/register">
                registrarte
              </Link>{" "}
              gratis.
            </p>

            <div className="mt-6 flex flex-col gap-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => loginWithGoogle()}
              >
                <img src="/fotos/google_logo.png" alt="Google" className="h-5 w-5" />
                Iniciar sesión con Google
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleMicrosoftLogin}
              >
                <img src="/fotos/microsoft_logo.svg" alt="Microsoft" className="h-5 w-5" />
                Iniciar sesión con Microsoft
              </Button>
            </div>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-sm text-muted-foreground">O</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {error && <p className="mb-4 text-center text-sm font-semibold text-destructive">{error}</p>}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label htmlFor="correo" className="mb-1.5 block text-sm font-medium text-foreground">
                  Correo Electrónico
                </label>
                <Input
                  type="email"
                  id="correo"
                  name="correo"
                  value={form.correo}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label htmlFor="contrasena" className="mb-1.5 block text-sm font-medium text-foreground">
                  Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    id="contrasena"
                    name="contrasena"
                    value={form.contrasena}
                    onChange={handleChange}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={toggleShowPassword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full">
                Iniciar sesión
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={handleRedirect}>
                Registrarse
              </Button>
            </form>

            <p className="mt-4 text-center text-sm">
              <Link to="/forgetpassword" className="text-primary hover:underline">
                ¿Olvidaste tu contraseña?
              </Link>
            </p>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Al continuar, aceptas las{" "}
              <Link className="text-primary hover:underline" to="/condiciones-uso">
                Condiciones de uso
              </Link>{" "}
              y el{" "}
              <Link className="text-primary hover:underline" to="/aviso-privacidad">
                Aviso de privacidad
              </Link>{" "}
              de Procu.
            </p>
            <p className="mt-4 text-center text-sm">
              ¿No tienes cuenta?{" "}
              <Link to="/register" className="font-semibold text-primary hover:underline">
                Regístrate
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default Login;
